/**
 * Owner: Masa Bayu
 * File: createLibraryModel.ts
 * Date:Time: 2026-09-22 05:20
 * Description: UI-03 + R-02 — Library model · honest CTA · active catalog covers.
 */

import type { QxreadLibraryItem } from '@qx/qxread-core';
import { getCatalogEntry } from '../../publication/catalog-port';
import { resolveDemoCoverUrl } from '../../publication/demo-cover-assets';
import {
  getDemoLibraryProjection,
  getDemoLibraryQueryState,
  getDemoLibrarySurface,
  getDemoQueriedLibraryItems,
} from '../demo-library-store';
import { getDemoLibraryLaunchContract } from '../demo-library-launch';
import type { LibraryPresentationModel, LibraryShelfCard } from './library-types';

function isHonestlyOpenable(item: QxreadLibraryItem): boolean {
  return (
    item.state !== 'blocked' &&
    item.state !== 'removed' &&
    item.state !== 'archived' &&
    item.state !== 'materializing'
  );
}

function toCard(item: QxreadLibraryItem): LibraryShelfCard {
  const entry = getCatalogEntry(item.publicationId);
  const rawAuthors = entry?.metadata.authors;
  const authors = Array.isArray(rawAuthors) ? rawAuthors : [];
  const rawCover = entry?.metadata.coverAssetRef;
  const coverUrl = resolveDemoCoverUrl(typeof rawCover === 'string' ? rawCover : null);
  const percent = item.progress.percentComplete;
  const updateAvailable = item.state === 'update_available';
  const baseProgress =
    item.progress.state === 'completed'
      ? 'Completed'
      : percent > 0
        ? `${String(percent)}%`
        : 'Not started';
  const progressLabel = updateAvailable
    ? `${baseProgress} · Update available`
    : baseProgress;
  const canOpen = isHonestlyOpenable(item) && entry !== null;
  const accessLabel = !canOpen
    ? entry === null
      ? 'Publication unavailable'
      : 'Access not granted'
    : updateAvailable
      ? 'Update available · opens latest eligible'
      : percent > 0 && item.progress.state !== 'completed'
        ? 'Continue'
        : 'Open';
  return {
    item,
    coverUrl,
    authors,
    progressLabel,
    canOpen,
    updateAvailable,
    accessLabel,
  };
}

export function createLibraryModel(deepLinkPath?: string | null): LibraryPresentationModel {
  const projection = getDemoLibraryProjection();
  const queried = getDemoQueriedLibraryItems();
  const queryState = getDemoLibraryQueryState();
  const surface = getDemoLibrarySurface();
  const launch = getDemoLibraryLaunchContract();

  const continueReading = projection.continueReading.map(toCard).filter((c) => c.canOpen);
  const shelf = queried.map(toCard);
  const completed = projection.completed.map(toCard);
  const blocked = projection.blocked.map(toCard);

  const launchHint =
    launch !== null
      ? `Launch · ${launch.validationState} · allowed=${String(launch.allowed)} · executesReader=false`
      : null;

  return {
    continueReading,
    shelf,
    completed,
    blocked,
    wishlist: projection.wishlist,
    query: queryState.query,
    filter: queryState.filter,
    sort: queryState.sort,
    itemCount: surface.itemCount,
    ownedCount: projection.purchased.length,
    empty: shelf.length === 0 && continueReading.length === 0 && projection.wishlist.length === 0,
    deepLinkPath: deepLinkPath ?? null,
    launchHint,
  };
}
